/*

& The website's side of a paid broadcast (client-safe: no models).

  CreateNewBroadcastModal reads the pricing (broadcast/fetch-broadcast-pricing; store billing, 1 Nov 2026):
  whether this business's next broadcast is free (one of its free broadcasts, or the paid switch off), the reach
  bands it can buy now, each with its breakdown shown as it is. A free broadcast reaches every eligible agent up to
  `free_reach`; a paid one picks a band on the agents step (NoOfAgentScreen) and Send first pays:
  broadcast/create-broadcast-order prices that band and opens a Razorpay order, Razorpay's checkout (loaded in
  pages/_document.tsx) takes the payment, and broadcast/verify-broadcast-payment checks it. The verified payment id
  then goes to broadcast/create-new-broadcast, which claims it (lib/broadcasts/broadcastPayment.ts).

  A checkout the person closes resolves as cancelled; Razorpay retries a failed attempt inside its
  own sheet, so only the last failure's reason is reported when it is closed.

*/

import { PriceBreakdownPayload } from "../payments/priceBreakdownPayload";

/** One reach band: up to `agents` agents for `price` (GST included); `breakdown` adds the fee and any offer. */
export interface BroadcastBandPayload {
	agents: number;
	price: number;
	product_id: string;
	breakdown: PriceBreakdownPayload;
}

export interface BroadcastPricingPayload {
	payment_enabled: boolean;
	/** True when this business's next broadcast costs nothing (the switch off, or a free broadcast left). */
	is_free: boolean;
	/** The business's free broadcasts; null while the switch is off. */
	free_allowance: { limit: number; used: number; remaining: number } | null;
	/** The most agents a free broadcast reaches; null when there is no cap. */
	free_reach: number | null;
	/** The agents this business can reach now; null without a business. */
	pool: number | null;
	currency: string;
	platform_fee: { type: "fixed" | "percentage"; value: number };
	tax: { tax_name: string; tax_percentage: number };
	tax_inclusive: boolean;
	/** The bands the business can buy now, smallest first. */
	bands: Array<BroadcastBandPayload>;
	/** The breakdown of the band asked about; null when none was. */
	breakdown: PriceBreakdownPayload | null;
	/** Each city asked for: its reach and whether it may be picked (under the smallest band it may not). */
	cities?: Array<{ city: string; agents: number; available: boolean }>;
}

/** True when Send has to pay first: this broadcast isn't free and its band costs something (an offer can make it free). */
export const broadcastNeedsPayment = (pricing: BroadcastPricingPayload | null): boolean =>
	!!pricing?.payment_enabled && pricing.is_free === false && (pricing.breakdown?.total || 0) > 0;

/** The band a paid broadcast buys by default: the largest one offered. Null when none is. */
export const defaultBroadcastBand = (pricing: BroadcastPricingPayload | null): BroadcastBandPayload | null =>
	pricing?.bands?.length ? pricing.bands[pricing.bands.length - 1] : null;

/** How many agents a broadcast reaches: a free one everyone up to `free_reach`, a paid one its band (never more than the pool). */
export function broadcastReach(pricing: BroadcastPricingPayload | null, pool: number, bandAgents: number): number {
	if (pricing?.is_free !== false) return pricing?.free_reach ? Math.min(pool, pricing.free_reach) : pool;
	return Math.min(pool, bandAgents);
}

export type BroadcastCheckoutResult =
	| { ok: true; paymentId: string }
	| { ok: false; cancelled: boolean; error: string };

async function postJson(route: string, body: Record<string, unknown>): Promise<any> {
	const res = await fetch(process.env.PRODUCTION_API_URL + route, {
		method: "POST",
		cache: "no-store",
		headers: { "Content-Type": "text/plain" },
		body: JSON.stringify(body),
	});
	return res.json();
}

/**
 * The broadcast pricing for [businessProfileId] (its free broadcasts and the bands it can buy), with the breakdown
 * of the band reaching [noOfAgents]; null when it can't be read (treated as free).
 */
export async function fetchBroadcastPricing(
	businessProfileId: string,
	noOfAgents: number,
	/* Cities (4 Nov 2026): the reach and bands for a broadcast to [city], and [cities] with whether each may be picked. */
	city?: string | null,
	cities?: Array<string>,
): Promise<BroadcastPricingPayload | null> {
	try {
		const response = await postJson("broadcast/fetch-broadcast-pricing", {
			business_profile_id: businessProfileId,
			no_of_agents: noOfAgents,
			...(city ? { city } : {}),
			...(cities && cities.length > 0 ? { cities } : {}),
		});
		return response?.status === "success" ? (response.payload as BroadcastPricingPayload) : null;
	} catch {
		return null;
	}
}

/**
 * Pays for a broadcast in the band of [noOfAgents] agents from [businessProfileId]: creates the order, opens
 * Razorpay's checkout and verifies the payment. Resolves with the verified payment id.
 */
export async function payForBroadcast(args: {
	businessProfileId: string;
	noOfAgents: number;
	prefill: { name: string; email: string; contact: string };
	themeColor: string;
	/* The city the broadcast goes to: the order's band must fit that city's reach (4 Nov 2026). */
	city?: string | null;
}): Promise<BroadcastCheckoutResult> {
	let order: any;
	try {
		order = await postJson("broadcast/create-broadcast-order", {
			business_profile_id: args.businessProfileId,
			no_of_agents: args.noOfAgents,
			...(args.city ? { city: args.city } : {}),
		});
	} catch {
		return { ok: false, cancelled: false, error: "Could not start the payment. Please try again." };
	}
	if (order?.status !== "success" || !order.payload?.order_id) {
		return { ok: false, cancelled: false, error: order?.error || "Could not start the payment. Please try again." };
	}

	const Window: any = typeof window !== "undefined" ? window : null;
	if (!Window || !Window.Razorpay) {
		return { ok: false, cancelled: false, error: "The payment window could not load. Please refresh and try again." };
	}

	return new Promise<BroadcastCheckoutResult>((resolve) => {
		let settled = false;
		let lastFailure = "";
		const settle = (result: BroadcastCheckoutResult) => {
			if (settled) return;
			settled = true;
			resolve(result);
		};

		const razorpay = new Window.Razorpay({
			key: order.payload.key_id,
			amount: order.payload.amount,
			currency: order.payload.currency,
			name: "Beegru",
			description: `Broadcast to ${order.payload.no_of_agents} agents`,
			order_id: order.payload.order_id,
			handler: async (response: any) => {
				try {
					const verified = await postJson("broadcast/verify-broadcast-payment", {
						razorpay_payment_id: response.razorpay_payment_id,
						razorpay_order_id: response.razorpay_order_id,
						razorpay_signature: response.razorpay_signature,
					});
					if (verified?.status === "success" && verified.payload?.payment_id) {
						settle({ ok: true, paymentId: String(verified.payload.payment_id) });
					} else {
						settle({
							ok: false,
							cancelled: false,
							error: verified?.error || "We couldn't confirm your payment. Please contact support if you were charged.",
						});
					}
				} catch {
					settle({
						ok: false,
						cancelled: false,
						error: "We couldn't confirm your payment. Please contact support if you were charged.",
					});
				}
			},
			modal: {
				ondismiss: () =>
					settle({
						ok: false,
						cancelled: lastFailure === "",
						error: lastFailure || "Payment cancelled",
					}),
			},
			prefill: args.prefill,
			notes: { order_id: order.payload.order_id },
			theme: { color: args.themeColor },
			retry: { enabled: true, max_count: 3 },
			redirect: false,
		});

		razorpay.on("payment.failed", (failure: any) => {
			lastFailure = failure?.error?.description || "The payment failed";
		});

		razorpay.open();
	});
}
